import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  CircularProgress,
  Box
} from '@material-ui/core'
import { GetApp as DownloadIcon } from '@material-ui/icons'

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0
  return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const ExportPreviewTab = ({ packageId, url }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!packageId) return
    setLoading(true)
    setError('')
    axios
      .get(`${url}/api/bid-estimation/bid-packages/${packageId}/export-preview`)
      .then(res => {
        setData(res.data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load export preview.')
        setLoading(false)
      })
  }, [packageId, url])

  const handleExport = () => {
    window.open(url + '/api/bid-estimation/bid-packages/' + packageId + '/export', '_blank')
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  const groups = (data && data.groups) || []
  const warnings = (data && data.warnings) || []
  const grandTotal = groups.reduce((sum, g) => sum + (parseFloat(g.subtotal) || 0), 0)

  return (
    <Box>
      {/* Top toolbar with export button */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>

      {/* Warnings section */}
      {warnings.length > 0 && (
        <Paper style={{ backgroundColor: '#fff3e0', padding: 16, marginBottom: 24 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
            Warnings
          </Typography>
          {warnings.map((warning, i) => (
            <Typography key={i} variant="body2" style={{ marginBottom: 4 }}>
              &bull; {warning}
            </Typography>
          ))}
        </Paper>
      )}

      {/* Groups */}
      {groups.map(group => (
        <Box key={group._id} mb={4}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
            {group.name || '(unnamed group)'}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Item No</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell align="right"><strong>Qty</strong></TableCell>
                  <TableCell align="right"><strong>Unit Price</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(group.items || []).map((item, idx) => {
                  const isZero = (!item.unitPrice || parseFloat(item.unitPrice) === 0) &&
                                 (!item.totalPrice || parseFloat(item.totalPrice) === 0)
                  const cellStyle = isZero ? { color: '#9e9e9e' } : {}
                  return (
                    <TableRow key={item._id || idx}>
                      <TableCell style={cellStyle}>{item.itemNo || ''}</TableCell>
                      <TableCell style={cellStyle}>{item.descriptionRaw || ''}</TableCell>
                      <TableCell style={cellStyle}>{item.unitRaw || ''}</TableCell>
                      <TableCell align="right" style={cellStyle}>
                        {item.quantity != null ? item.quantity : ''}
                      </TableCell>
                      <TableCell align="right" style={cellStyle}>
                        {formatCurrency(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right" style={cellStyle}>
                        {formatCurrency(item.totalPrice)}
                      </TableCell>
                    </TableRow>
                  )
                })}

                {/* Subtotal row */}
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    <strong>Subtotal</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(group.subtotal)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {/* Grand total */}
      {groups.length > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={2} mb={2}>
          <Paper variant="outlined" style={{ padding: '8px 24px' }}>
            <Typography variant="subtitle1">
              <strong>Grand Total: {formatCurrency(grandTotal)}</strong>
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export default ExportPreviewTab
